import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
  type Dispatch,
} from "react";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { PlanSummary } from "../../../types/planning/plan.type";
import { useSelectedServiceCenter } from "../../../hooks/useSelectedServiceCenter";
import { useDateSearchParamOrFallbackToToday } from "../../../hooks/useDateSearchParamOrFallbackToToday";
import { dateToInt } from "../../../utils/dateTimeHelper";
import { usePlannedRoutes } from "../../../hooks/react-query/usePlannedRoutes";
import { useShipmentDetailSearch } from "../../../hooks/react-query/preplanning/useShipmentDetails";

import { Shipment } from "../../../services/prePlanningService.types";
import { useUnassignedShipmentIds } from "../../../hooks/react-query/useUnassignedShipments";
import { useDeliveryRoutesSequenceStatus } from "../../../hooks/react-query/useDeliveryRoutesSequenceStatus";
import { useAutoSequenceRoutes } from "../../../hooks/react-query/useAutoSequenceRoute";
import { onAutoSequenceRoutesFinished } from "./utils";

type InboundPlanningContextValue = {
  plannedRoutes: PlanSummary[];
  shipments: Shipment[];
  isLoading: boolean;
  refetchPlans: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<PlanSummary[], Error>>;
  refetchShipments: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<Shipment[], Error>>;
  setAutoSequenceTimestamp: Dispatch<React.SetStateAction<number>>;
};

type InboundPlanningProviderType = {
  children: ReactNode;
};

const InboundPlanningContext =
  createContext<InboundPlanningContextValue | null>(null);

export const useInboundPlanningContext = (): InboundPlanningContextValue => {
  const context = useContext(InboundPlanningContext);
  if (!context) {
    throw new Error(
      "useInboundPlanningContext must be used within a InboundPlanningProvider"
    );
  }
  return context;
};

export const InboundPlanningProvider = ({
  children,
}: InboundPlanningProviderType) => {
  const [serviceCenter] = useSelectedServiceCenter();
  const [selectedDate] = useDateSearchParamOrFallbackToToday();
  const planDate = dateToInt(selectedDate);
  const [autoSequenceTimestamp, setAutoSequenceTimestamp] = useState(
    new Date().getTime()
  );

  const {
    data: unassignedShipmentIds = [],
    isLoading: isUnassignedShipmentsLoading,
  } = useUnassignedShipmentIds(serviceCenter.id, planDate);

  const {
    data: plannedRoutes = [],
    isLoading: isPlannedRoutesLoading,
    refetch: refetchPlans,
  } = usePlannedRoutes(planDate, serviceCenter.id);

  const {
    data: shipments = [],
    isLoading: isShipmentsDetailsLoading,
    refetch: refetchShipments,
  } = useShipmentDetailSearch({
    search: {
      shipmentIds: [
        ...new Set(plannedRoutes.flatMap(({ shipmentIds }) => shipmentIds)),
        ...unassignedShipmentIds.map(Number),
      ],
      sicId: serviceCenter.id,
      planDate,
      addPlanningInfo: true,
    },
    enabled: !isUnassignedShipmentsLoading,
    onlyIncludeHighlightedRows: true,
  });

  const { mutateAsync: autoSequenceRoutes, isPending: isAutoSequencing } =
    useAutoSequenceRoutes();

  useDeliveryRoutesSequenceStatus({
    routeIds: plannedRoutes
      .filter(
        (route) =>
          route.type === "delivery_route" &&
          route.sequenceStatus === "processing"
      )
      .map((route) => route.id),
    timestamp: autoSequenceTimestamp,
    onAutoSequenceRoutesFinished: async (result) => {
      await refetchPlans();
      await refetchShipments();

      onAutoSequenceRoutesFinished({
        planDate: dateToInt(selectedDate),
        sicId: serviceCenter.id,
        sequenceResult: result,
        retryMutation: autoSequenceRoutes,
      });
    },
  });

  const isLoading =
    isAutoSequencing ||
    isUnassignedShipmentsLoading ||
    isPlannedRoutesLoading ||
    isShipmentsDetailsLoading;

  const inboundPlanningContextValue = useMemo(
    () => ({
      plannedRoutes,
      shipments,
      isLoading,
      refetchPlans,
      refetchShipments,
      setAutoSequenceTimestamp,
    }),
    [plannedRoutes, shipments, isLoading, refetchPlans, refetchShipments]
  );

  return (
    <InboundPlanningContext.Provider value={inboundPlanningContextValue}>
      {children}
    </InboundPlanningContext.Provider>
  );
};
